import { FC, useCallback, useMemo, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0AvatarModule } from '@factorialco/f0-react'
import { Widget } from '@factorialco/f0-react/dist/experimental'
import { Check as CheckIcon } from '@factorialco/f0-react/icons/app'
import { DailyClockInWidgetDocument, DailyClockInWidgetQuery } from 'generated/resources/graphql'
import moment from 'moment'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import { buildWidget } from 'modules/core/lib/widgets'

import { useShiftActions } from 'modules/attendance/hooks/shifts/useShiftActions'
import { trackDailyClockIn } from 'modules/attendance/lib/tracking'
import AttendanceShiftUtils, { HalfDayTypes } from 'modules/attendance/lib/utils/AttendanceShift'
import { NEW_HOME_WIDGETS, NEW_HOME_WIDGET_ACTION_TYPES } from 'modules/dashboard/lib/events'
import { moduleAvailableToModuleId } from 'modules/upsell/lib/moduleIcons'

import { styles } from './index.styles'
import './index.styles.css'

const useLinkAndTitle = () => {
  const title = i18n.t('new_dashboard.clock_in.title')

  const { navigation } = useNavigation()

  const linkParams = {
    periodType: 'monthly',
    year: moment().year(),
    month: moment().month() + 1,
    day: moment().date(),
  }

  const linkUrl = navigation.clockIn.periodTypeYearMonthDay.fullPath(linkParams)

  const handleUpperRightActionClick = () => {
    track(trackingEvents.dashboard.clickOnHomeWidgetAction, {
      widget: NEW_HOME_WIDGETS.CLOCK_IN,
      actionType: NEW_HOME_WIDGET_ACTION_TYPES.UPPER_RIGHT_BUTTON,
    })
  }

  const link = {
    url: linkUrl,
    title,
    onClick: handleUpperRightActionClick,
  }

  return {
    link,
    title,
  }
}

type PreparedData = {
  todayShifts: NonNullable<
    DailyClockInWidgetQuery['attendance']['employee']
  >['attendanceShiftsConnection']['nodes']
  hasForfaitJours: boolean
}

const prepareData = (data: DailyClockInWidgetQuery): PreparedData => {
  const contract = data.contracts.referenceContractsConnection.nodes[0]
  const employee = data.attendance.employee
  const todayShifts =
    employee?.attendanceShiftsConnection.nodes.filter(
      (shift) => shift.workable && AttendanceShiftUtils.isHalfDay(shift)
    ) || []

  return {
    todayShifts,
    hasForfaitJours: Boolean(contract?.frForfaitJours),
  }
}

const DailyClockInWidgetComponent: FC<{
  data: PreparedData
  employeeId: number
  onClickWidget?: () => void
}> = ({ data: { todayShifts, hasForfaitJours }, employeeId }) => {
  const { link, title } = useLinkAndTitle()
  const { createShift, deleteShift } = useShiftActions()

  const [isCreating, setIsCreating] = useState(false)

  const today = moment().format('YYYY-MM-DD')

  const firstHalfShift = todayShifts.find((shift) => AttendanceShiftUtils.isFirstHalf(shift))
  const secondHalfShift = todayShifts.find((shift) => AttendanceShiftUtils.isSecondHalf(shift))

  const isFirstHalfChecked = Boolean(firstHalfShift)
  const isSecondHalfChecked = Boolean(secondHalfShift)

  const daysWorked = useMemo(() => {
    return todayShifts.reduce((acc: number, shift) => {
      return acc + AttendanceShiftUtils.halfDayDuration(shift)
    }, 0)
  }, [todayShifts])

  const handleFirstHalfChange = useCallback(
    async (checked: boolean) => {
      if (isCreating) return

      setIsCreating(true)

      try {
        if (checked && !firstHalfShift) {
          await createShift({
            date: today,
            referenceDate: today,
            halfDay: HalfDayTypes.BEGINNING_OF_DAY,
            employeeId,
            workable: true,
            fetchDependencies: false,
          })
          if (hasForfaitJours) {
            trackDailyClockIn('desktop_daily_widget')
          }
        } else if (!checked && firstHalfShift?.id) {
          await deleteShift({
            id: firstHalfShift.id,
            date: today,
            fetchDependencies: false,
          })
        }
      } finally {
        setIsCreating(false)
      }
    },
    [isCreating, firstHalfShift, today, employeeId, createShift, deleteShift]
  )

  const handleSecondHalfChange = useCallback(
    async (checked: boolean) => {
      if (isCreating) return

      setIsCreating(true)

      try {
        if (checked && !secondHalfShift) {
          await createShift({
            date: today,
            referenceDate: today,
            halfDay: HalfDayTypes.END_OF_DAY,
            employeeId,
            workable: true,
            fetchDependencies: false,
          })
          if (hasForfaitJours) {
            trackDailyClockIn('desktop_daily_widget')
          }
        } else if (!checked && secondHalfShift?.id) {
          await deleteShift({
            id: secondHalfShift.id,
            date: today,
            fetchDependencies: false,
          })
        }
      } finally {
        setIsCreating(false)
      }
    },
    [isCreating, secondHalfShift, today, employeeId, createShift, deleteShift]
  )

  return (
    <Widget
      header={{
        title,
        link,
      }}
    >
      <Box className={'DailyClockIn_container'} id="daily-clock-in-widget">
        <Box className={'DailyClockIn_iconAndTextContainer'}>
          <F0AvatarModule module={moduleAvailableToModuleId('clock-in')} size="lg" />
          <Box className={'DailyClockIn_todayText'}>
            {i18n.t('clock_in.observations.modal.today')}
          </Box>
          <Box className={'DailyClockIn_daysWorkedText'}>
            {daysWorked === 0
              ? i18n.t('clock_in.observations.modal.no_record_yet')
              : daysWorked === 0.5
                ? i18n.t('clock_in.observations.modal.0_5_days')
                : i18n.t('clock_in.observations.modal.1_day')}
          </Box>
        </Box>

        <Box className={'DailyClockIn_halfDayRowsContainer'}>
          <Box
            className={
              isFirstHalfChecked
                ? styles.halfDayRowCheckedCombined
                : styles.halfDayRowUncheckedCombined
            }
            onClick={(e) => {
              e.stopPropagation()
              handleFirstHalfChange(!isFirstHalfChecked)
            }}
          >
            <Box
              className={
                isFirstHalfChecked
                  ? styles.checkboxCheckedCombined
                  : styles.checkboxUncheckedCombined
              }
            >
              {isFirstHalfChecked && (
                <CheckIcon width={18} height={18} className={'DailyClockIn_checkIcon'} />
              )}
            </Box>
            <Box className={'DailyClockIn_halfDayText'}>
              {i18n.t('clock_in.observations.modal.first_half')}
            </Box>
          </Box>

          <Box
            className={
              isSecondHalfChecked
                ? styles.halfDayRowCheckedCombined
                : styles.halfDayRowUncheckedCombined
            }
            onClick={(e) => {
              e.stopPropagation()
              handleSecondHalfChange(!isSecondHalfChecked)
            }}
          >
            <Box
              className={
                isSecondHalfChecked
                  ? styles.checkboxCheckedCombined
                  : styles.checkboxUncheckedCombined
              }
            >
              {isSecondHalfChecked && (
                <CheckIcon width={18} height={18} className={'DailyClockIn_checkIcon'} />
              )}
            </Box>
            <Box className={'DailyClockIn_halfDayText'}>
              {i18n.t('clock_in.observations.modal.second_half')}
            </Box>
          </Box>
        </Box>
      </Box>
    </Widget>
  )
}

const DailyClockInWidgetWrapper: FC<{
  data: PreparedData
  employeeId: number
  onClickWidget?: () => void
}> = ({ data, employeeId, onClickWidget }) => {
  return (
    <DailyClockInWidgetComponent
      data={data}
      employeeId={employeeId}
      onClickWidget={onClickWidget}
    />
  )
}

export const buildDailyClockInWidget = () =>
  buildWidget({
    id: 'daily-clock-in',
    document: DailyClockInWidgetDocument,
    isEmpty: ({ hasForfaitJours }) => !hasForfaitJours,
    prepareData,
    component: DailyClockInWidgetWrapper,
  })
